/**
 * Gathering — node-level breakout-room planning, pure logic only.
 *
 * A gathering is described by:
 *   - groups: admin-defined, and people mark themselves into them (stored in
 *     gathering_memberships). A person can pick several — groups overlap.
 *   - an ideal room: a size, plus how many seats in it go to each group. Seats
 *     not reserved for a group are open to anyone in the pool.
 *
 * planRooms() turns that into concrete rooms; describeRoom() reads a room back
 * against the ideal (which seat each person fills, how the mix compares). Both
 * are deterministic for a given seed, so "reshuffle" is just a new seed and a
 * plan can be reproduced exactly. The admin view plans here and publishes the
 * result to the backend (pods/gatherings).
 *
 * Nothing here knows about React or the API.
 */

// ---------------------------------------------------------------------------
// Seeded randomness
// ---------------------------------------------------------------------------

// mulberry32 — tiny, fast, and good enough to shuffle a guest list.
export function seededRandom(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function shuffled(list, rand) {
    const out = [...list];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}

export const newSeed = () => Math.floor(Math.random() * 2 ** 31);

// ---------------------------------------------------------------------------
// Planning
// ---------------------------------------------------------------------------

/**
 * @param pool        person ids to seat
 * @param groups      [{ id }] in display order
 * @param membersOf   Map(groupId -> Set(personId))
 * @param size        ideal people per room
 * @param mix         { [groupId]: seats per room }
 * @param seed        integer; same inputs + seed => same plan
 * @returns {
 *   rooms: [{ index, personIds, quota: { [groupId]: n } }],
 *   demand, supply: { [groupId]: n } across all rooms,
 * }
 *
 * Room count is ceil(pool / size), with people spread evenly, so rooms come
 * out at `size` or one short — never one oversized room of leftovers.
 *
 * Group seats are filled scarcest group first (lowest supply / demand), and
 * within a group, people who belong to the fewest *other* reserved groups go
 * first. That keeps someone who is in both "Engineering" and "London" free
 * for whichever of those actually runs short — the whole point of allowing
 * overlap. Everyone left over takes an open seat, preferring a room where one
 * of their groups is still under its ideal count.
 */
export function planRooms({ pool, groups, membersOf, size, mix, seed }) {
    const rand = seededRandom(seed);
    const n = pool.length;
    const roomSize = Math.max(1, size | 0);
    if (n === 0) return { rooms: [], demand: {}, supply: {} };

    const roomCount = Math.max(1, Math.ceil(n / roomSize));
    const base = Math.floor(n / roomCount);
    const extra = n % roomCount;
    // Which rooms get the extra person varies with the seed too.
    const roomOrder = shuffled([...Array(roomCount).keys()], rand);
    const capacity = Array.from({ length: roomCount }, (_, r) => base + (roomOrder.indexOf(r) < extra ? 1 : 0));

    const poolSet = new Set(pool);
    const reserved = groups.filter(g => (mix[g.id] || 0) > 0);
    const supply = Object.fromEntries(groups.map(g => [
        g.id, [...(membersOf.get(g.id) || [])].filter(id => poolSet.has(id)).length,
    ]));

    // Per-room quotas: the ideal mix, trimmed when a room comes out smaller
    // than the ideal size. The most plentiful group gives up a seat first,
    // since it's the one most likely to fill it from open seats anyway.
    const quotas = capacity.map(cap => {
        const q = Object.fromEntries(reserved.map(g => [g.id, mix[g.id]]));
        let total = Object.values(q).reduce((a, b) => a + b, 0);
        while (total > cap) {
            const give = reserved
                .filter(g => q[g.id] > 0)
                .sort((a, b) => (supply[b.id] / mix[b.id]) - (supply[a.id] / mix[a.id]))[0];
            q[give.id] -= 1;
            total -= 1;
        }
        return q;
    });
    const demand = Object.fromEntries(reserved.map(g => [g.id, quotas.reduce((a, q) => a + q[g.id], 0)]));

    const rooms = capacity.map((_, index) => ({ index, personIds: [], quota: quotas[index] }));
    const seated = new Set();
    const groupsOf = id => reserved.filter(g => membersOf.get(g.id)?.has(id)).map(g => g.id);

    const scarcity = g => (demand[g.id] ? supply[g.id] / demand[g.id] : Infinity);
    const reservedOrder = [...reserved].sort((a, b) => scarcity(a) - scarcity(b));

    for (const g of reservedOrder) {
        const candidates = shuffled([...(membersOf.get(g.id) || [])].filter(id => poolSet.has(id)), rand)
            .map(id => ({ id, others: groupsOf(id).length - 1 }))
            .sort((a, b) => a.others - b.others) // stable: random within a tier
            .map(c => c.id);
        // Round-robin the seats, so a shortfall is spread one-per-room
        // instead of leaving the last rooms with none of this group.
        const slots = [];
        const most = Math.max(0, ...quotas.map(q => q[g.id]));
        for (let k = 0; k < most; k++) {
            for (const r of roomOrder) if (quotas[r][g.id] > k) slots.push(r);
        }
        let c = 0;
        for (const r of slots) {
            while (c < candidates.length && seated.has(candidates[c])) c++;
            if (c >= candidates.length) break;
            if (rooms[r].personIds.length >= capacity[r]) continue;
            rooms[r].personIds.push(candidates[c]);
            seated.add(candidates[c]);
            c++;
        }
    }

    // Open seats. Count a room's current members per group (overlap counts
    // for every group a person is in) to see where each person helps most.
    const countIn = (room, gid) => room.personIds.filter(id => membersOf.get(gid)?.has(id)).length;
    for (const id of shuffled(pool.filter(id => !seated.has(id)), rand)) {
        const mine = groupsOf(id);
        let best = null;
        let bestScore = -Infinity;
        for (const r of roomOrder) {
            const room = rooms[r];
            const free = capacity[r] - room.personIds.length;
            if (free <= 0) continue;
            const helps = mine.filter(gid => countIn(room, gid) < room.quota[gid]).length;
            const score = helps * 100 + free;
            if (score > bestScore) { bestScore = score; best = room; }
        }
        (best || rooms[0]).personIds.push(id);
        seated.add(id);
    }

    return { rooms, demand, supply };
}

/**
 * A room read back against its quota — computed, never stored, so a manual
 * drag between rooms is instantly re-explained.
 *
 * @returns {
 *   seats: { [personId]: groupId | null }  — the reserved seat each person
 *          fills (null = an open seat),
 *   mix:   [{ groupId, target, seated, present }] — `seated` counts people
 *          filling that group's seats, `present` counts every member in the
 *          room (overlap included),
 *   met:   true when every reserved seat is filled,
 * }
 */
export function describeRoom(room, groups, membersOf) {
    const seats = Object.fromEntries(room.personIds.map(id => [id, null]));
    const reserved = groups.filter(g => (room.quota[g.id] || 0) > 0);
    const inGroup = (id, gid) => !!membersOf.get(gid)?.has(id);
    const presentOf = gid => room.personIds.filter(id => inGroup(id, gid)).length;

    // Same rule as planning: tightest group first, specialists first.
    const order = [...reserved].sort((a, b) => (presentOf(a.id) - room.quota[a.id]) - (presentOf(b.id) - room.quota[b.id]));
    for (const g of order) {
        let need = room.quota[g.id];
        const candidates = room.personIds
            .filter(id => seats[id] === null && inGroup(id, g.id))
            .sort((a, b) => reserved.filter(x => inGroup(a, x.id)).length - reserved.filter(x => inGroup(b, x.id)).length);
        for (const id of candidates) {
            if (need <= 0) break;
            seats[id] = g.id;
            need -= 1;
        }
    }

    const mix = groups.map(g => ({
        groupId: g.id,
        target: room.quota[g.id] || 0,
        seated: Object.values(seats).filter(s => s === g.id).length,
        present: presentOf(g.id),
    }));
    return { seats, mix, met: mix.every(m => m.seated >= m.target) };
}

/**
 * Whether two room line-ups seat the same people in the same rooms — order
 * within a room doesn't matter. Used to tell the admin whether what they see
 * is what members see.
 */
export function sameRooms(a = [], b = []) {
    if (a.length !== b.length) return false;
    const key = r => [...r.personIds].sort((x, y) => x - y).join(',');
    return a.every((r, i) => key(r) === key(b[i]));
}

/**
 * The topic room `index` gets: topics in order, looping once every topic has
 * been used; extra topics simply go unused. Mirrors topic_for() in
 * apps/backend/app/pods/gatherings/service.py, which assigns them on publish.
 */
export const topicFor = (index, topics = []) => (topics.length ? topics[index % topics.length] : null);

// ---------------------------------------------------------------------------
// Rounds: avoiding people meeting twice
// ---------------------------------------------------------------------------

const pairKey = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);

/** Every pair of people who share a room, as a Set of "a:b" keys. */
export function roomPairs(rooms) {
    const pairs = new Set();
    for (const r of rooms) {
        const ids = r.personIds;
        for (let i = 0; i < ids.length; i++) for (let j = i + 1; j < ids.length; j++) pairs.add(pairKey(ids[i], ids[j]));
    }
    return pairs;
}

/** How many pairs in `rooms` already met in an earlier round (`seen`). */
export function countRepeats(rooms, seen) {
    let n = 0;
    for (const p of roomPairs(rooms)) if (seen.has(p)) n++;
    return n;
}

/**
 * A seed for a later breakout round: tries `tries` candidates and keeps the
 * one whose rooms meet the ideal mix most often, then repeat the fewest
 * pairs from earlier rounds. Deterministic from `from`, so the choice can be
 * explained and reproduced.
 */
export function pickSeed({ planArgs, seen, groups, membersOf, tries = 40, from = newSeed() }) {
    const rand = seededRandom(from);
    let best = null;
    for (let i = 0; i < tries; i++) {
        const seed = Math.floor(rand() * 2 ** 31);
        const { rooms } = planRooms({ ...planArgs, seed });
        const met = rooms.filter(r => describeRoom(r, groups, membersOf).met).length;
        const repeats = seen.size ? countRepeats(rooms, seen) : 0;
        if (!best || met > best.met || (met === best.met && repeats < best.repeats)) best = { seed, met, repeats };
        if (!seen.size) break; // first round: nothing to avoid, any seed will do
    }
    return best.seed;
}

// ---------------------------------------------------------------------------
// Flow timing
// ---------------------------------------------------------------------------

/** How long a step runs this time: its planned length plus any extension. */
export const stepMinutes = s => s.duration_minutes + (s.extended_minutes || 0);

/**
 * Steps with their start/end, in minutes from the start of the flow,
 * including any time added to a step during the run.
 */
export function flowTimeline(steps) {
    let t = 0;
    return steps.map(s => {
        const start = t;
        t += stepMinutes(s);
        return { ...s, start, end: t };
    });
}

/**
 * Where a running flow is at `now` (ms): the live step's index and how much
 * of it is left, or `finished`. `startedAt` is ms too. Null when not started.
 * For a paused flow pass the pause time as `now` — the position freezes
 * there, and resuming moves `startedAt` forward by the pause (server-side).
 */
export function flowPosition(steps, startedAt, now) {
    if (startedAt == null) return null;
    const elapsedMin = Math.max(0, (now - startedAt) / 60000);
    const timeline = flowTimeline(steps);
    const index = timeline.findIndex(s => elapsedMin < s.end);
    if (index === -1) return { index: steps.length, finished: true, remainingMs: 0, elapsedMin };
    return { index, finished: false, remainingMs: (timeline[index].end - elapsedMin) * 60000, elapsedMin };
}

/** "12:05" / "1:02:05" for a countdown. */
export function formatCountdown(ms) {
    const total = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** Server timestamps are naive UTC; parse them as such, to ms. */
export const parseServerTime = at => (at ? new Date(/[zZ+]/.test(at) ? at : `${at}Z`).getTime() : null);

/** The join link for a gathering hangout room (GatheringCall.jsx). */
export const gatheringCallUrl = (roomName, label) =>
    `/gathering/call?room=${encodeURIComponent(roomName)}&label=${encodeURIComponent(label)}`;

/**
 * Where a member should be for step `index` of their schedule (the member
 * view's `schedule`): the common room for a 'common' step, their own room for
 * a breakout round. `roomName` is the LiveKit room to join — null while
 * there's nothing to join yet (LiveKit not set up, or the member isn't seated
 * this round, in which case `room` is null too). `key` changes whenever the
 * place to be does — including a republish moving someone mid-step — and
 * only then, so a client can tell "same room" from "time to move". The
 * common room is one room for the whole run, so back-to-back common steps
 * share a key: nobody gets bounced out and back in between them.
 */
export function stopFor(schedule, index, commonRoomName) {
    const step = schedule?.[index];
    if (!step) return null;
    const room = step.kind === 'breakout' ? step.room ?? null : null;
    const roomName = step.kind === 'common' ? commonRoomName || null : room?.hangout_room_name || null;
    const key = step.kind === 'common' ? `common:${roomName ?? ''}` : `${step.step_id}:${roomName ?? ''}`;
    return { index, stepId: step.step_id, kind: step.kind, room, roomName, key };
}
